import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Loader2, FileText } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { DemoNotSavedNotice } from '@/components/demo/DemoNotSavedNotice'

interface NoteSummaryProps {
    formData: any
    onEdit: () => void
    onSubmit: (notificationMessage: string) => void
    onNavigateHome: () => void
    showConfirm: boolean
    onConfirmNavigateHome: () => void
    onCancelConfirm: () => void
}

export const NoteSummary = ({
    formData,
    onEdit,
    onSubmit,
    onNavigateHome,
    showConfirm,
    onConfirmNavigateHome,
    onCancelConfirm
}: NoteSummaryProps) => {
    const [notificationMessage, setNotificationMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const hasMentions = (formData.noteData?.mentionedWorkerIds?.length || 0) > 0

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            await onSubmit(notificationMessage)
        } catch (error: any) {
            console.error('Error submitting note:', error)
            const msg = error?.message || 'Une erreur est survenue.'
            alert(`${msg}\n\nNe rechargez pas la page — vos données sont conservées et vous pouvez réessayer.`)
            setIsSubmitting(false)
        }
    }

    const formatValue = (value: any) => {
        if (Array.isArray(value)) {
            return value.join(', ')
        }
        return value?.toString() || ''
    }

    const keyTextMap: Record<string, string> = {
        locataire: "Nom du.de la locataire",
        travailleur: "Travailleur.euse",
        collegue: "Collègue.s HF présent.e.s",
        date: "Date de la note"
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader title="Récapitulatif - Note" icon={<FileText size={20} />} onBack={onNavigateHome} />
            <div className="max-w-4xl mx-auto py-8 px-4">
                <div className="space-y-6 mb-8">
                    {/* Information sur la note */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Information sur la note</h2>
                        <div className="space-y-2">
                            {Object.entries(formData.noteData || {}).map(([key, value]) => {
                                if (key === 'uuid' || key === 'submit_ts' || key === 'comment' || key === 'note' || key === 'mentionedWorkerIds' || key === 'mentionedWorkerNames') return null

                                // Skip empty values
                                if (!value || (Array.isArray(value) && value.length === 0)) return null
                                return (
                                    <div key={key}>
                                        <span className="font-medium">{keyTextMap[key] || key} :</span>{' '}
                                        {formatValue(value)}
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Note content */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Contenu de la note</h2>
                        <div
                            className="prose max-w-none p-4 border border-gray-200 rounded-md bg-gray-50 min-h-[200px]"
                            dangerouslySetInnerHTML={{ __html: formData.noteData?.note || '' }}
                        />
                    </div>
                </div>

                {/* Final form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
                    {hasMentions && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h3 className="text-sm font-medium text-gray-800 mb-2">Notification</h3>
                            <p className="text-xs text-gray-500 mb-1">
                                Les personnes suivantes recevront une notification :
                            </p>
                            <div className="flex flex-wrap gap-1 mb-3">
                                {(formData.noteData?.mentionedWorkerNames || []).map((name: string) => (
                                    <span key={name} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                        @{name}
                                    </span>
                                ))}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Message <span className="text-gray-400 font-normal">(optionnel)</span>
                                </label>
                                <textarea
                                    value={notificationMessage}
                                    onChange={(e) => setNotificationMessage(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Ex: Merci de regarder le point sur le bailleur..."
                                />
                            </div>
                        </div>
                    )}

                    <DemoNotSavedNotice />
                    <div className="flex justify-between">
                        <Button type="button" variant="outline" onClick={onEdit}>
                            Modifier la note
                        </Button>
                        <div className="space-x-4">
                            <Button type="button" variant="outline" onClick={onNavigateHome}>
                                Accueil
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sauvegarde...
                                    </>
                                ) : (
                                    'Valider'
                                )}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>

            {showConfirm && (
                <ConfirmDialog
                    onConfirm={onConfirmNavigateHome}
                    onCancel={onCancelConfirm}
                />
            )}
        </div>
    )
}
